import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import { Flex } from "../../../../../components/Flex";
import { Text } from "../../../../../components/Text";
import { useTranslation } from "react-i18next";
import { TestIDs } from "../../../../../tests/types/testIds";
import { Textarea } from "../../../../../components/Textarea";
import { Lengths } from "../../../../../model/shared/enum/Lengths.enum";
import { Input } from "../../../../../components/Input";
import { Tooltip } from "../../../../../utils/local-antd";
import styled from "styled-components";
import { AtiraIcon } from "../../../../../components/AtiraIcon";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons/faInfoCircle";
import { Clickable } from "../../../../../components/Clickable";
import { faTrash } from "@fortawesome/free-solid-svg-icons/faTrash";
import { faPlus } from "@fortawesome/free-solid-svg-icons/faPlus";
import { Button } from "../../../../../components/Button";
import { faFile } from "@fortawesome/free-regular-svg-icons/faFile";
import { Spacing } from "../../../../../theme/Spacing";

const StyledInfo = styled(Tooltip)`
  cursor: help;
`;

const Container = styled(Flex)`
  gap: ${Spacing.xl};
  width: 50%;
  flex-wrap: wrap;
  align-self: flex-start;
  flex-direction: column;
`;

export const SettingsAIChatBotStepData = () => {
  const { t } = useTranslation();
  const { control } = useFormContext();

  const { fields, append, remove } = useFieldArray({
    control,
    name: "aiFaqPairs",
  });

  const canEnableChatBot = true;

  return (
    <Container>
      <Flex alignItems="center" gap="s">
        <AtiraIcon icon={faFile} color="main" size="xl" />

        <Text fontSize="l" fontWeight={"bold"} color="main">
          {t("settings.aiChatbot.sections.data.title")}
        </Text>
      </Flex>

      <Controller
        name="aiConfig.aiPromptTemplate"
        control={control}
        render={({ fieldState: { error }, field: { onChange, value } }) => (
          <Textarea
            value={value}
            onChange={onChange}
            title={t("settings.aiChatbot.prompt.promptTitle")}
            placeholder={t("settings.aiChatbot.prompt.promptPlaceholder")}
            info={t("settings.aiChatbot.prompt.promptInfo")}
            valid={!error?.message}
            errorMessage={error?.message}
            maxLength={Lengths.LONG}
            rows={4}
            dataTestId={TestIDs.chatbot.drawer.createEdit.fields.prompt}
          />
        )}
      />

      <Controller
        name={`aiConfig.aiCrawledDataSources.${0}`}
        control={control}
        render={({ fieldState: { error }, field: { onChange, value } }) => (
          <Input
            value={value || ""}
            onChange={onChange}
            title={t(
              "settings.aiChatbot.crawledDataSources.crawledDataSourcesTitle",
            )}
            placeholder={t("settings.aiChatbot.crawledDataSources.placeholder")}
            info={t(
              "settings.aiChatbot.crawledDataSources.crawledDataSourcesInfo",
            )}
            valid={!error?.message}
            errorMessage={error?.message}
            maxLength={Lengths.LONG}
            dataTestId={TestIDs.chatbot.drawer.createEdit.fields.dataSource}
          />
        )}
      />

      <Controller
        name="aiConfig.aiFAQs"
        control={control}
        render={() => (
          <Flex flexDirection="column" gap="m">
            <Flex alignItems="center" gap="s">
              <StyledInfo title={t("settings.aiChatbot.faq.title.info")}>
                <span>
                  <AtiraIcon icon={faInfoCircle} color="main" size="sm" />
                </span>
              </StyledInfo>
              <Text>{t("settings.aiChatbot.faq.title")}</Text>
              <Text fontSize="s" color="gray">
                ({t("common.optional")})
              </Text>
            </Flex>

            <Flex flexDirection="column" gap="l">
              {fields.map((field, index) => (
                <Flex key={field.id} gap="m" alignItems="start">
                  <Flex flexDirection="column" gap="s" width={"100%"}>
                    <Flex flexDirection="column" gap="s">
                      <Controller
                        control={control}
                        name={`aiFaqPairs.${index}.question`}
                        render={({ field: { value, onChange } }) => (
                          <Input
                            disabled={!canEnableChatBot}
                            value={value || ""}
                            onChange={onChange}
                            placeholder={t(
                              "settings.aiChatbot.faq.question.input.title",
                              { index: index + 1 },
                            )}
                            dataTestId={TestIDs.chatbot.drawer.createEdit.fields.faqQuestion(
                              index,
                            )}
                          />
                        )}
                      />

                      <Controller
                        control={control}
                        name={`aiFaqPairs.${index}.answer`}
                        render={({ field: { value, onChange } }) => (
                          <Textarea
                            disabled={!canEnableChatBot}
                            value={value || ""}
                            onChange={onChange}
                            placeholder={t(
                              "settings.aiChatbot.faq.question.input.placeholder",
                              { index: index + 1 },
                            )}
                            dataTestId={TestIDs.chatbot.drawer.createEdit.fields.faqAnswer(
                              index,
                            )}
                          />
                        )}
                      />
                    </Flex>
                  </Flex>
                  <Flex flexDirection="column" gap="m" alignItems="center">
                    <Clickable
                      onClick={() => remove(index)}
                      disabled={!canEnableChatBot}
                      dataTestId={TestIDs.chatbot.drawer.createEdit.fields.faqButtonRemove(
                        index,
                      )}
                    >
                      <Tooltip title={t("common.delete")}>
                        <span />
                        <AtiraIcon icon={faTrash} color="main" />
                      </Tooltip>
                    </Clickable>
                  </Flex>
                </Flex>
              ))}
            </Flex>

            <Button
              disabled={!canEnableChatBot}
              icon={faPlus}
              margin="none"
              width="18rem"
              onClick={() => append({ question: "", answer: "" })}
              dataTestId={TestIDs.chatbot.drawer.createEdit.fields.faqButtonAdd}
            >
              {t("settings.aiChatbot.faq.addButton")}
            </Button>
          </Flex>
        )}
      />
    </Container>
  );
};
